import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  Divider,
  Button,
  TextField,
  FormControlLabel,
  Avatar,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Container,
  Card,
  CardContent,
} from '@mui/material';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../theme/ThemeProvider';
import Save from '@mui/icons-material/Save';
import Close from '@mui/icons-material/Close';
import Logout from '@mui/icons-material/Logout';
import ContentCopy from '@mui/icons-material/ContentCopy';
import CreditCard from '@mui/icons-material/CreditCard';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { Check, Sparkles, Shield, Zap } from 'lucide-react';
import CalendarManagement from '../../components/CalendarManagement';
import PhoneVerification from '../../components/PhoneVerification';
import ReferralCode from '../../components/ReferralCode';
import { FeatureErrorBoundary } from '../../components/FeatureErrorBoundary';
import { useSearchParams } from 'react-router-dom';
import { useSettings } from '../../hooks/useSettings';
import DashboardLayout from '../../components/DashboardLayout';
import { ACCOUNT_STATUS } from '../../@shared/interfaces';
import { userService } from '../../services/api/user.service';

// Declare Stripe Pricing Table web component for TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'stripe-pricing-table': React.DetailedHTMLProps<
        React.HTMLAttributes<Element> & {
          'pricing-table-id': string;
          'publishable-key': string;
          'customer-session-client-secret': string;
        },
        Element
      >;
    }
  }
}

// Styled components using theme colors
const SettingsContainer = styled(Container)`
  padding: ${(props) => props.theme.spacing.md};
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: ${(props) => props.theme.spacing.sm};
  }
`;

const Subtitle = styled(Typography)`
  color: ${(props) => props.theme.colors.text.secondary};
  font-size: ${(props) => props.theme.typography.body.default.size};
`;

const StyledTabs = styled(Tabs)`
  margin-bottom: ${(props) => props.theme.spacing.lg};

  .MuiTabs-root {
    border-bottom: 1px solid ${(props) => props.theme.colors.input.border};
  }

  .MuiTab-root {
    color: ${(props) => props.theme.colors.text.secondary};
    font-weight: 500;
    text-transform: none;
    font-size: ${(props) => props.theme.typography.body.default.size};

    &:hover {
      color: ${(props) => props.theme.colors.primary};
    }

    &.Mui-selected {
      color: ${(props) => props.theme.colors.info};
    }
  }

  .MuiTabs-indicator {
    background-color: ${(props) => props.theme.colors.info};
    height: 3px;
    border-radius: 2px;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    .MuiTab-root {
      min-width: auto;
      padding: 12px 8px;
      font-size: 0.875rem;
    }
  }
`;

const ContentCard = styled(Card)`
  background: ${(props) => props.theme.colors.black2};
  border: 2px solid ${(props) => props.theme.colors.input.border};
  border-radius: 20px;
  box-shadow: none;
  margin-bottom: ${(props) => props.theme.spacing.lg};
`;

const SectionTitle = styled(Typography)`
  color: ${(props) => props.theme.colors.text.primary};
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: ${(props) => props.theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
`;

const StyledTextField = styled(TextField)`
  margin-bottom: ${(props) => props.theme.spacing.md};

  .MuiOutlinedInput-root {
    background-color: ${(props) => props.theme.colors.input.background};
    border-radius: 12px;

    fieldset {
      border-color: ${(props) => props.theme.colors.input.border};
    }

    &:hover fieldset {
      border-color: ${(props) => props.theme.colors.primary};
    }

    &.Mui-focused fieldset {
      border-color: ${(props) => props.theme.colors.primary};
      border-width: 2px;
    }

    &.Mui-disabled {
      background-color: ${(props) => props.theme.colors.input.background}80;

      fieldset {
        border-color: ${(props) => props.theme.colors.text.secondary}40;
      }

      input {
        color: ${(props) => props.theme.colors.text.secondary};
        -webkit-text-fill-color: ${(props) =>
          props.theme.colors.text.secondary};
      }
    }
  }

  .MuiInputLabel-root {
    color: ${(props) => props.theme.colors.text.secondary};

    &.Mui-focused {
      color: ${(props) => props.theme.colors.primary};
    }

    &.Mui-disabled {
      color: ${(props) => props.theme.colors.text.secondary}80;
    }
  }

  .MuiInputBase-input {
    color: ${(props) => props.theme.colors.text.primary};
  }

  .MuiFormHelperText-root {
    color: ${(props) => props.theme.colors.text.secondary};
    margin-left: 4px;
  }
`;

const ProfileSection = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${(props) => props.theme.spacing.xl};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const AvatarSection = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${(props) => props.theme.spacing.lg};
  width: 100%;
`;

const FormSection = styled(Box)`
  width: 100%;
  max-width: 600px;
  box-sizing: border-box;
`;

const PrimaryButton = styled(Button)`
  background: ${(props) => props.theme.gradients.primary};
  color: ${(props) => props.theme.colors.white};
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  min-height: 48px;

  &:hover {
    background: ${(props) => props.theme.gradients.primary};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${(props) => props.theme.colors.primary}40;
  }

  &:disabled {
    background: ${(props) => props.theme.colors.text.secondary}40;
    color: ${(props) => props.theme.colors.text.secondary};
    transform: none;
    box-shadow: none;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    width: 100%;
  }
`;

const SecondaryButton = styled(Button)`
  border: 2px solid ${(props) => props.theme.colors.input.border};
  color: ${(props) => props.theme.colors.text.primary};
  background: transparent;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  min-height: 48px;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
    background: ${(props) => props.theme.colors.primary}10;
    transform: translateY(-1px);
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    width: 100%;
  }
`;

const LogoutButton = styled(Button)`
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: ${(props) => props.theme.colors.white};
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  text-transform: none;
  min-height: 48px;

  &:hover {
    background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px #dc354540;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    width: 100%;
  }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-bottom: ${(props) => props.theme.spacing.md};

  .MuiFormControlLabel-label {
    color: ${(props) => props.theme.colors.text.primary};
    font-weight: 500;
  }

  .MuiSwitch-track {
    background-color: ${(props) => props.theme.colors.input.border};
  }

  .MuiSwitch-thumb {
    background-color: ${(props) => props.theme.colors.white};
  }

  .Mui-checked {
    .MuiSwitch-thumb {
      background-color: ${(props) => props.theme.colors.white};
    }

    & + .MuiSwitch-track {
      background-color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const HelperText = styled(Typography)`
  color: ${(props) => props.theme.colors.text.secondary};
  font-size: ${(props) => props.theme.typography.body.small.size};
  margin-left: ${(props) => props.theme.spacing.lg};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const StyledDivider = styled(Divider)`
  margin: ${(props) => props.theme.spacing.xl} 0;
  background-color: ${(props) => props.theme.colors.input.border};
`;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

// Export TAB_CONFIG for use in redirect service and other components
export const SETTINGS_TAB_CONFIG = {
  profile: {
    label: 'Profile',
    index: 0,
    key: 'profile' as const,
  },
  accounts: {
    label: 'Accounts',
    index: 1,
    key: 'accounts' as const,
  },
  billing: {
    label: 'Billing',
    index: 2,
    key: 'billing' as const,
  },
  preferences: {
    label: 'Preferences',
    index: 3,
    key: 'preferences' as const,
  },
} as const;

// Internal alias for backwards compatibility
const TAB_CONFIG = SETTINGS_TAB_CONFIG;

export type SettingsTabKey = keyof typeof SETTINGS_TAB_CONFIG;
type TabKey = SettingsTabKey;

/**
 * BillingSection - Conditionally renders billing management or upgrade prompt
 * Backend provides customerPortalUrl in profile when user has active subscription
 */
const BillingSection: React.FC<{
  accountStatus?: ACCOUNT_STATUS;
  customerPortalUrl?: string;
}> = ({ accountStatus, customerPortalUrl }) => {
  const [customerClientSecret, setCustomerClientSecret] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isManageHovered, setIsManageHovered] = useState(false);

  const pricingTableId =
    import.meta.env.VITE_STRIPE_PRICING_TABLE_ID ||
    'prctbl_1P5JwnFD7rhtxAD8uknjYyR8';
  const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';

  // FREE users should see the pricing table embedded
  const isFreeUser = accountStatus === ACCOUNT_STATUS.FREE;
  const isPremiumUser = accountStatus !== ACCOUNT_STATUS.FREE;

  useEffect(() => {
    const generateCustomerSecret = async () => {
      if (!isFreeUser) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await userService.generateCustomerSecret();

        if (response.success && response.customerClientSecret) {
          setCustomerClientSecret(response.customerClientSecret);
        } else {
          setError(response.message || 'Failed to initialize pricing table');
        }
      } catch (err) {
        console.error('Error generating customer secret:', err);
        setError('An error occurred while loading pricing information');
      } finally {
        // Simulate loading for demo purposes
        setTimeout(() => {
          setIsLoading(false);
        }, 1500);
      }
    };

    generateCustomerSecret();
  }, [isFreeUser]);

  const handleManageSubscription = () => {
    if (customerPortalUrl) {
      window.open(customerPortalUrl, '_blank');
    }
  };

  return (
    <>
      {isPremiumUser ? (
        <>
          {/* Premium Plan Card */}
          <Box
            sx={{
              background:
                'linear-gradient(135deg, rgba(109, 83, 159, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
              border: '1px solid rgba(109, 83, 159, 0.15)',
              borderRadius: '16px',
              padding: '2rem',
              marginBottom: '2rem',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '2rem',
                flexWrap: 'wrap',
                gap: '1rem',
              }}
            >
              <Box
                sx={{
                  fontSize: '1.75rem',
                  fontWeight: 600,
                  color: '#F0F3FF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  margin: 0,
                }}
              >
                <Sparkles size={28} style={{ color: '#E4CB76' }} />
                Keysha AI Premium
                <Box
                  sx={{
                    background:
                      'linear-gradient(135deg, #6D539F 0%, #3B82F6 100%)',
                    color: '#F0F3FF',
                    padding: '0.375rem 0.875rem',
                    borderRadius: '24px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {!isFreeUser ? 'Active' : 'Upgrade for Premium'}
                </Box>
              </Box>
              <Box
                sx={{
                  fontSize: '2.5rem',
                  fontWeight: 700,
                  color: '#F0F3FF',
                  lineHeight: 1,
                }}
              >
                $8
                <Box
                  component="span"
                  sx={{
                    fontSize: '1.125rem',
                    color: '#9C9C9D',
                    fontWeight: 400,
                    marginLeft: '0.25rem',
                  }}
                >
                  /week
                </Box>
              </Box>
            </Box>

            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                marginTop: '2rem',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '1rem',
                padding: 0,
              }}
            >
              {[
                'Schedule your todo list',
                'Create events and invite others',
                'Productivity Dashboard',
                'SMS Reminders',
                'AI Chat Assistant',
                'GPT Integration',
              ].map((feature, index) => (
                <Box
                  key={index}
                  component="li"
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    color: '#F0F3FF',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                  }}
                >
                  <Check
                    size={20}
                    style={{
                      color: '#6D539F',
                      flexShrink: 0,
                      marginTop: '0.125rem',
                    }}
                  />
                  {feature}
                </Box>
              ))}
            </Box>
          </Box>

          <Button
            sx={{
              background:
                'linear-gradient(135deg, #6D539F 0%, #3B82F6 100%) !important',
              color: '#F0F3FF !important',
              border: 'none !important',
              padding: '0.875rem 2rem !important',
              borderRadius: '10px !important',
              fontSize: '1rem !important',
              fontWeight: '600 !important',
              textTransform: 'none !important',
              transition: 'all 0.3s ease !important',
              display: 'inline-flex',
              alignItems: 'center',
              transform: isManageHovered ? 'translateY(-2px)' : 'none',
              boxShadow: isManageHovered
                ? '0 10px 30px rgba(109, 83, 159, 0.4)'
                : 'none',
            }}
            onMouseEnter={() => setIsManageHovered(true)}
            onMouseLeave={() => setIsManageHovered(false)}
            onClick={handleManageSubscription}
          >
            Manage Subscription
            <ChevronRight sx={{ fontSize: 18 }} />
          </Button>
        </>
      ) : (
        <>
          <Box
            sx={{
              borderRadius: '16px',
              marginTop: '0',
              minHeight: '600px',
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'center',
              width: '100%',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '500px',
                margin: '0 auto',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: 'transparent',
                  borderRadius: '16px',
                  padding: '1rem',
                  minHeight: '550px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      width: '50px',
                      height: '50px',
                      border: '3px solid rgba(109, 83, 159, 0.2)',
                      borderTopColor: '#6D539F',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      '@keyframes spin': {
                        to: { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                ) : error ? (
                  <>
                    <Typography
                      sx={{
                        color: '#C75B7A',
                        mb: 1,
                        fontSize: '1.25rem',
                        fontWeight: 600,
                      }}
                    >
                      {error}
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', color: '#9C9C9D' }}>
                      Please refresh the page or contact support
                    </Typography>
                  </>
                ) : customerClientSecret ? (
                  <>
                    {/* Add comprehensive CSS for Stripe pricing table dark theme */}
                    <style>{`
                      /* Stripe CSS Custom Properties */
                      stripe-pricing-table {
                        --background: transparent !important;
                        --text: #F0F3FF !important;
                        --text-secondary: #9C9C9D !important;
                        --border: rgba(109, 83, 159, 0.15) !important;
                        --primary: linear-gradient(135deg, #6D539F 0%, #3B82F6 100%) !important;
                        --card-background: linear-gradient(135deg, rgba(109, 83, 159, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%) !important;
                        --card-border: rgba(109, 83, 159, 0.15) !important;
                      }
                      
                      /* Universal Stripe element styling */
                      stripe-pricing-table,
                      stripe-pricing-table *,
                      stripe-pricing-table::before,
                      stripe-pricing-table::after {
                        background-color: transparent !important;
                        color: #F0F3FF !important;
                        border-color: rgba(109, 83, 159, 0.15) !important;
                      }
                      
                      /* Main container */
                      stripe-pricing-table {
                        background: transparent !important;
                        border: none !important;
                        border-radius: 16px !important;
                        overflow: hidden !important;
                      }
                      
                      /* Pricing cards */
                      stripe-pricing-table [class*="card"],
                      stripe-pricing-table [class*="pricing"],
                      stripe-pricing-table [class*="plan"] {
                        background: linear-gradient(135deg, rgba(109, 83, 159, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%) !important;
                        border: 1px solid rgba(109, 83, 159, 0.15) !important;
                        border-radius: 16px !important;
                        backdrop-filter: blur(10px) !important;
                        box-shadow: none !important;
                      }
                      
                      /* Headers and titles */
                      stripe-pricing-table h1,
                      stripe-pricing-table h2,
                      stripe-pricing-table h3,
                      stripe-pricing-table h4,
                      stripe-pricing-table [class*="title"],
                      stripe-pricing-table [class*="header"],
                      stripe-pricing-table [class*="name"] {
                        color: #F0F3FF !important;
                        background: transparent !important;
                      }
                      
                      /* Price elements */
                      stripe-pricing-table [class*="price"],
                      stripe-pricing-table [class*="amount"],
                      stripe-pricing-table [class*="cost"] {
                        color: #F0F3FF !important;
                        background: transparent !important;
                      }
                      
                      /* Buttons */
                      stripe-pricing-table button,
                      stripe-pricing-table [role="button"],
                      stripe-pricing-table [class*="button"],
                      stripe-pricing-table [class*="cta"] {
                        background: linear-gradient(135deg, #6D539F 0%, #3B82F6 100%) !important;
                        color: #F0F3FF !important;
                        border: none !important;
                        border-radius: 10px !important;
                        font-weight: 600 !important;
                        transition: all 0.3s ease !important;
                        text-decoration: none !important;
                      }
                      
                      stripe-pricing-table button:hover,
                      stripe-pricing-table [role="button"]:hover,
                      stripe-pricing-table [class*="button"]:hover,
                      stripe-pricing-table [class*="cta"]:hover {
                        transform: translateY(-2px) !important;
                        box-shadow: 0 10px 30px rgba(109, 83, 159, 0.4) !important;
                        background: linear-gradient(135deg, #7A619F 0%, #4A92F6 100%) !important;
                      }
                      
                      /* Feature lists */
                      stripe-pricing-table ul,
                      stripe-pricing-table li,
                      stripe-pricing-table [class*="feature"],
                      stripe-pricing-table [class*="benefit"] {
                        color: #F0F3FF !important;
                        background: transparent !important;
                      }
                      
                      stripe-pricing-table li::before,
                      stripe-pricing-table [class*="check"],
                      stripe-pricing-table [class*="tick"] {
                        color: #6D539F !important;
                        background: transparent !important;
                      }
                      
                      /* Badges and labels */
                      stripe-pricing-table [class*="badge"],
                      stripe-pricing-table [class*="label"],
                      stripe-pricing-table [class*="tag"] {
                        background: linear-gradient(135deg, #6D539F 0%, #3B82F6 100%) !important;
                        color: #F0F3FF !important;
                        border: none !important;
                        border-radius: 24px !important;
                      }
                      
                      /* Toggle switches and controls */
                      stripe-pricing-table [class*="toggle"],
                      stripe-pricing-table [class*="switch"],
                      stripe-pricing-table input {
                        background: rgba(26, 26, 36, 0.4) !important;
                        border: 1px solid rgba(109, 83, 159, 0.15) !important;
                        color: #F0F3FF !important;
                      }
                      
                      /* Secondary text */
                      stripe-pricing-table [class*="description"],
                      stripe-pricing-table [class*="subtitle"],
                      stripe-pricing-table small {
                        color: #9C9C9D !important;
                        background: transparent !important;
                      }
                    `}</style>
                    <stripe-pricing-table
                      pricing-table-id={pricingTableId}
                      publishable-key={publishableKey}
                      customer-session-client-secret={customerClientSecret}
                    />
                  </>
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      color: '#9C9C9D',
                      background:
                        'linear-gradient(135deg, rgba(109, 83, 159, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)',
                      border: '1px solid rgba(109, 83, 159, 0.25)',
                      borderRadius: '16px',
                      padding: '2rem',
                      backdropFilter: 'blur(10px)',
                      width: '100%',
                      maxWidth: '400px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        color: '#F0F3FF',
                        marginBottom: '0.5rem',
                      }}
                    >
                      Stripe Pricing Table
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.95rem',
                        marginTop: '0.5rem',
                        color: '#9C9C9D',
                      }}
                    >
                      Start with a free trial, no credit card required
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* Info Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
          marginTop: '3rem',
        }}
      >
        <Box
          sx={{
            background: 'rgba(26, 26, 36, 0.3)',
            border: '1px solid rgba(109, 83, 159, 0.08)',
            borderRadius: '12px',
            padding: '1.75rem',
            backdropFilter: 'blur(5px)',
          }}
        >
          <Typography
            sx={{
              color: '#F0F3FF',
              fontSize: '1.125rem',
              fontWeight: 600,
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Shield size={20} style={{ color: '#6D539F' }} />
            Secure Payment
          </Typography>
          <Typography
            sx={{
              color: '#9C9C9D',
              fontSize: '0.925rem',
              lineHeight: 1.7,
              margin: 0,
            }}
          >
            Your payment information is securely processed through Stripe. We
            never store your credit card details on our servers.
          </Typography>
        </Box>

        <Box
          sx={{
            background: 'rgba(26, 26, 36, 0.3)',
            border: '1px solid rgba(109, 83, 159, 0.08)',
            borderRadius: '12px',
            padding: '1.75rem',
            backdropFilter: 'blur(5px)',
          }}
        >
          <Typography
            sx={{
              color: '#F0F3FF',
              fontSize: '1.125rem',
              fontWeight: 600,
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <Zap size={20} style={{ color: '#3B82F6' }} />
            Cancel Anytime
          </Typography>
          <Typography
            sx={{
              color: '#9C9C9D',
              fontSize: '0.925rem',
              lineHeight: 1.7,
              margin: 0,
            }}
          >
            No commitments, no contracts. You can upgrade, downgrade, or cancel
            your subscription at any time from your billing portal.
          </Typography>
        </Box>

        <Box
          sx={{
            background: 'rgba(26, 26, 36, 0.3)',
            border: '1px solid rgba(109, 83, 159, 0.08)',
            borderRadius: '12px',
            padding: '1.75rem',
            backdropFilter: 'blur(5px)',
          }}
        >
          <Typography
            sx={{
              color: '#F0F3FF',
              fontSize: '1.125rem',
              fontWeight: 600,
              marginBottom: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <CreditCard sx={{ color: '#E4CB76', fontSize: 20 }} />
            Billing Support
          </Typography>
          <Typography
            sx={{
              color: '#9C9C9D',
              fontSize: '0.925rem',
              lineHeight: 1.7,
              margin: 0,
            }}
          >
            Need help with billing? Contact our support team and we'll be happy
            to assist you with any questions or concerns.
          </Typography>
        </Box>
      </Box>
    </>
  );
};

const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    profile,
    isLoading,
    formData,
    notifications,
    snackbar,
    isUpdatingProfile,
    handleInputChange,
    handleNotificationChange,
    hideSnackbar,
    saveName,
    isPhoneVerified,
    phoneNumber,
    referralCode,
  } = useSettings();

  // Get public profile link from user profile (no separate API call needed)
  const publicProfileLink = profile?.publicProfileLink;

  const [activeTab, setActiveTab] = useState<number>(() => {
    const tab = searchParams.get('tab') as TabKey;
    // DONT CHANGE THIS it will break the calendar oauth redirect - keep default tab index as 1, the accounts tab
    return tab && TAB_CONFIG[tab] ? TAB_CONFIG[tab].index : 1;
  });

  const [copied, setCopied] = useState(false);

  // Log component mount once
  useEffect(() => {
    console.log('[RedirectDebug] 🔄 SettingsPage component mounted');
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    const tabKey = Object.entries(TAB_CONFIG).find(
      ([_, config]) => config.index === newValue
    )?.[0];
    if (tabKey) {
      setSearchParams({ tab: tabKey });
    }
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleCopyProfileLink = async () => {
    if (publicProfileLink) {
      try {
        await navigator.clipboard.writeText(publicProfileLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
      } catch (err) {
        console.error('Failed to copy to clipboard:', err);
      }
    }
  };

  const handleSaveProfile = useCallback(async () => {
    try {
      await saveName();
      // Profile data is automatically updated via Zustand
    } catch (error) {
      // Error handling is already done in the saveName function
      console.error('Profile update failed:', error);
    }
  }, [saveName]);

  // Calendar management callbacks
  const handleCalendarRemoved = useCallback(() => {
    // UI updates automatically via Zustand profile refresh
    console.log('Calendar removed successfully');
  }, []);

  const handleCalendarUpdated = useCallback(() => {
    // UI updates automatically via Zustand profile refresh
    console.log('Calendar updated successfully');
  }, []);

  // Phone verification callback
  const handlePhoneVerified = useCallback((_phone: string) => {
    // UI updates automatically via useSettings hook
    console.log('Phone verified successfully');
  }, []);

  if (isLoading) {
    return (
      <DashboardLayout showChatButton={false}>
        <SettingsContainer>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="50vh"
          >
            <Typography color={theme.colors.text.primary}>
              Loading settings...
            </Typography>
          </Box>
        </SettingsContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout showChatButton={false}>
      <SettingsContainer>
        <StyledTabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {Object.entries(TAB_CONFIG).map(([_, config]) => (
            <Tab key={config.index} label={config.label} />
          ))}
        </StyledTabs>

        <CustomTabPanel value={activeTab} index={0}>
          {/* Mobile-responsive container for Profile tab */}
          <Box
            sx={{
              minHeight: '100vh',
              backgroundColor: 'rgb(26, 26, 46)',
              borderRadius: '8px',
              margin: {
                xs: '-24px -16px 0 -16px', // Mobile: account for container padding
                sm: '-24px -24px 0 -24px', // Desktop: fill container seamlessly
              },
              padding: {
                xs: '0.5rem 1rem 2rem 1rem', // Mobile: minimal top padding
                sm: '0.5rem 1.5rem 2rem 1.5rem', // Desktop: slightly more side padding
              },
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: '100vw',
              boxSizing: 'border-box',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                maxWidth: '1000px',
                margin: '0 auto',
                width: '100%',
                flex: 1,
              }}
            >
              {/* Phone Verification Section - First */}
              <PhoneVerification
                currentPhone={phoneNumber}
                isVerified={isPhoneVerified}
                onVerificationComplete={handlePhoneVerified}
              />

              {/* Profile Information - Second */}
              <Box
                sx={{
                  backgroundColor: 'rgb(26, 26, 46)',
                  border: '2px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  marginBottom: { xs: '1rem', sm: '1.5rem' },
                  padding: { xs: '1rem', sm: '1.5rem' },
                }}
              >
                <ProfileSection>
                  <AvatarSection>
                    <Avatar
                      src={profile?.photoUrl || user?.picture}
                      alt={profile?.firstName || user?.name || 'User'}
                      sx={{
                        width: { xs: 60, sm: 90, md: 120 },
                        height: { xs: 60, sm: 90, md: 120 },
                        fontSize: { xs: 20, sm: 30, md: 50 },
                        border: '4px solid',
                        borderColor: theme.colors.primary,
                        boxShadow: `0 0 30px ${theme.colors.primary}40`,
                        bgcolor: theme.colors.input.background,
                        color: theme.colors.primary,
                        fontWeight: 700,
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: isLoading ? 0.6 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                      }}
                    >
                      {!profile?.photoUrl &&
                        !user?.picture &&
                        (profile?.firstName?.[0] ||
                          user?.name?.[0] ||
                          user?.email?.[0] ||
                          'U')}
                    </Avatar>
                  </AvatarSection>

                  <FormSection>
                    <Box display="flex" flexDirection="column" gap={3}>
                      <Box
                        display="flex"
                        gap={3}
                        flexDirection={{ xs: 'column', sm: 'row' }}
                      >
                        <Box flex={1}>
                          <StyledTextField
                            fullWidth
                            label="First Name"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            variant="outlined"
                          />
                        </Box>
                        <Box flex={1}>
                          <StyledTextField
                            fullWidth
                            label="Last Name"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                      <StyledTextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        variant="outlined"
                        disabled
                        helperText="Email cannot be changed"
                      />
                      <StyledTextField
                        fullWidth
                        label="Bio"
                        name="bio"
                        value={formData.bio}
                        onChange={handleInputChange}
                        variant="outlined"
                        multiline
                        rows={4}
                        placeholder="Tell us a little about yourself"
                      />

                      {/* Profile Link Section */}
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          border: `1px solid ${theme.colors.text.secondary}`,
                          borderRadius: 2,
                          backgroundColor: theme.colors.background,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: theme.colors.text.primary,
                              fontWeight: 600,
                            }}
                          >
                            Your Profile Link
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<ContentCopy />}
                            onClick={handleCopyProfileLink}
                            disabled={!publicProfileLink}
                            sx={{
                              color: theme.colors.primary,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              minWidth: 'auto',
                            }}
                          >
                            {copied ? 'Copied!' : 'Copy'}
                          </Button>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.colors.text.secondary,
                            fontFamily: 'monospace',
                            backgroundColor: theme.colors.background,
                            p: 1,
                            borderRadius: 1,
                            border: `1px solid ${theme.colors.text.secondary}`,
                          }}
                        >
                          {publicProfileLink || 'Loading...'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.colors.text.secondary,
                            mt: 1,
                            display: 'block',
                          }}
                        >
                          Share this link to let others connect with you
                        </Typography>
                      </Box>
                    </Box>
                  </FormSection>
                </ProfileSection>

                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: '0.75rem', sm: '1rem' },
                    justifyContent: 'flex-end',
                    padding: { xs: '1rem', sm: '1.5rem' },
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '0 0 20px 20px',
                    margin: {
                      xs: '0 -1rem -1rem -1rem',
                      sm: '0 -1.5rem -1.5rem -1.5rem',
                    },
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                  }}
                >
                  <SecondaryButton variant="outlined" startIcon={<Close />}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSaveProfile}
                    disabled={isUpdatingProfile}
                  >
                    {isUpdatingProfile ? 'Saving...' : 'Save Changes'}
                  </PrimaryButton>
                </Box>
              </Box>

              {/* Referral Code - Third */}
              <Box
                sx={{
                  backgroundColor: 'rgb(26, 26, 46)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  marginBottom: { xs: '1rem', sm: '1.5rem' },
                  padding: { xs: '1rem', sm: '1.5rem' },
                }}
              >
                <ReferralCode referralCode={referralCode} showTitle={true} />
              </Box>
            </Box>
          </Box>
        </CustomTabPanel>

        <CustomTabPanel value={activeTab} index={1}>
          <ContentCard>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle>Connected Accounts</SectionTitle>
              <Subtitle style={{ marginBottom: theme.spacing.lg }}>
                Manage third-party services connected to your account.
              </Subtitle>
              <CalendarManagement
                onCalendarRemoved={handleCalendarRemoved}
                onCalendarUpdated={handleCalendarUpdated}
              />
            </CardContent>
          </ContentCard>
        </CustomTabPanel>

        <CustomTabPanel value={activeTab} index={2}>
          <Box
            sx={{
              minHeight: '100vh',
              backgroundColor: 'rgb(26, 26, 46)',
              borderRadius: '8px',
              margin: {
                xs: '-24px -16px 0 -16px', // Mobile: account for container padding
                sm: '-24px -24px 0 -24px', // Desktop: fill container seamlessly
              },
              padding: {
                xs: '0.5rem 1rem 2rem 1rem', // Mobile: minimal top padding
                sm: '0.5rem 1.5rem 2rem 1.5rem', // Desktop: slightly more side padding
              },
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                maxWidth: '1000px',
                margin: '0 auto',
                width: '100%',
                flex: 1,
              }}
            >
              {/* Header Section */}
              <Box sx={{ mb: 3, pt: 1 }}>
                {' '}
                {/* Even more reduced spacing */}
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2rem', sm: '2.5rem' }, // Responsive font size
                    fontWeight: 700,
                    color: '#F0F3FF',
                    mb: 1,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Billing & Subscription
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.125rem' }, // Responsive font size
                    color: '#9C9C9D',
                    margin: 0,
                    fontWeight: 400,
                    lineHeight: 1.6,
                  }}
                >
                  Manage your subscription and billing preferences
                </Typography>
              </Box>

              {/* Billing Section */}
              <FeatureErrorBoundary
                fallback={
                  <Box
                    sx={{
                      background: 'rgba(244, 67, 54, 0.1)',
                      border: '1px solid rgba(244, 67, 54, 0.3)',
                      borderRadius: '16px',
                      padding: '2rem',
                      textAlign: 'center',
                    }}
                  >
                    <Typography variant="h6" color="error" gutterBottom>
                      Unable to load billing information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Please refresh the page or contact support if this
                      persists.
                    </Typography>
                  </Box>
                }
              >
                <BillingSection
                  accountStatus={profile?.settings?.accountStatus}
                  customerPortalUrl={
                    (profile?.settings?.stripe as any)?.customerPortalUrl
                  }
                />
              </FeatureErrorBoundary>
            </Box>

            {/* Logout Button at Bottom */}
            <Box
              sx={{
                maxWidth: '1000px',
                margin: '0 auto',
                width: '100%',
                pt: 4,
                pb: 2,
                borderTop: '1px solid rgba(109, 83, 159, 0.15)',
                mt: 4,
              }}
            >
              <LogoutButton
                variant="contained"
                startIcon={<Logout />}
                onClick={handleLogout}
                sx={{
                  width: { xs: '100%', sm: 'auto' }, // Full width on mobile
                }}
              >
                Logout
              </LogoutButton>
            </Box>
          </Box>
        </CustomTabPanel>

        <CustomTabPanel value={activeTab} index={3}>
          <ContentCard>
            <CardContent sx={{ p: 3 }}>
              <SectionTitle>Notification Settings</SectionTitle>

              <StyledFormControlLabel
                control={
                  <Switch
                    checked={notifications.email}
                    onChange={handleNotificationChange}
                    name="email"
                    color="primary"
                  />
                }
                label="Email Notifications"
              />
              <HelperText>
                Receive email updates about your account activity
              </HelperText>

              <StyledFormControlLabel
                control={
                  <Switch
                    checked={notifications.push}
                    onChange={handleNotificationChange}
                    name="push"
                    color="primary"
                  />
                }
                label="Push Notifications"
              />
              <HelperText>
                Receive push notifications in your browser
              </HelperText>

              <StyledDivider />

              <SectionTitle>Communication Preferences</SectionTitle>

              <StyledFormControlLabel
                control={
                  <Switch
                    checked={notifications.marketing}
                    onChange={handleNotificationChange}
                    name="marketing"
                    color="primary"
                  />
                }
                label="Marketing Communications"
              />
              <HelperText>
                Receive updates about new features and promotions
              </HelperText>
            </CardContent>
          </ContentCard>
        </CustomTabPanel>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={hideSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={hideSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </SettingsContainer>
    </DashboardLayout>
  );
};

export default SettingsPage;
